import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { APP_BUILDER_BUILD_TAB_PATH, CRM_CLIENTS_PAGE } from 'src/constants';
import { RouteContext } from 'src/context';
import useNavigate from 'src/hooks/useNavigate';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { togglePrimarySidebarMobile } from 'src/store/ui/actions';

import {
  EXPLORE_PORTAL_TASK_ID,
  GUIDED_TOUR_FIRST_CLIENT,
  GUIDED_TOUR_PARAM,
  STUDIO_CHECKLIST_TASK_IDS,
  StudioChecklistTask,
  StudioChecklistTaskId,
  StudioChecklistTaskStatus,
} from './studioChecklist';

const TASK_ROUTES: Record<StudioChecklistTaskId, string> = {
  publishApp: APP_BUILDER_BUILD_TAB_PATH,
  // Carry the guided-tour intent so the CRM page starts the coachmark tour on arrival.
  explorePortal: `${CRM_CLIENTS_PAGE.path}?${GUIDED_TOUR_PARAM}=${GUIDED_TOUR_FIRST_CLIENT}`,
};

export interface UseStudioChecklistResult {
  tasks: StudioChecklistTask[];
  /** Whole-number completion percentage (0-100) for the progress bar. */
  completionPct: number;
  isCollapsed: boolean;
  toggleCollapse: () => void;
  /** Navigates to the task's destination route. */
  onTaskClick: (taskId: StudioChecklistTaskId) => void;
  /** Navigates to a route and closes the primary sidebar on mobile. */
  navigateAndCloseMobileSidebar: (path: string) => void;
  shouldRender: boolean;
}

export const useStudioChecklist = (): UseStudioChecklistResult => {
  const { navigate } = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isPrimarySidebarOpenOnMobile = useAppSelector(
    (state) => state.ui.primarySideBarMobile.isOpen,
  );

  // Collapse is ephemeral UI state — not worth persisting.
  const [isCollapsed, setIsCollapsed] = useState(false);
  const toggleCollapse = useCallback(() => setIsCollapsed((prev) => !prev), []);

  // TODO: statuses will come from the backend once it reports studio task state.
  // Until then they start empty (every task `todo`); clicking "Explore the client
  // experience" flips it to in-progress, and nothing reaches `done` yet, so the
  // hide-on-complete gate below cannot fire.
  const [statuses, setStatuses] = useState<
    Partial<Record<StudioChecklistTaskId, StudioChecklistTaskStatus>>
  >({});

  // Advance "explore the client experience" whenever the CRM clients page is
  // opened through the first-client guided tour. Reading the query param here
  // (rather than only in onTaskClick) covers every entry point — the checklist
  // click, the client "Return to CRM" card, and the post-publish modal.
  const { query } = useContext(RouteContext);
  const isFirstClientGuidedTour =
    query[GUIDED_TOUR_PARAM] === GUIDED_TOUR_FIRST_CLIENT;
  useEffect(() => {
    if (!isFirstClientGuidedTour) {
      return;
    }
    setStatuses((prev) =>
      prev[EXPLORE_PORTAL_TASK_ID]
        ? prev
        : {
            ...prev,
            [EXPLORE_PORTAL_TASK_ID]: StudioChecklistTaskStatus.InProgress,
          },
    );
  }, [isFirstClientGuidedTour]);

  const navigateAndCloseMobileSidebar = useCallback(
    (path: string) => {
      navigate(path);
      // Match sidebar nav items: close the primary sidebar on mobile after navigating.
      if (isPrimarySidebarOpenOnMobile) {
        dispatch(togglePrimarySidebarMobile({ isOpen: false }));
      }
    },
    [navigate, dispatch, isPrimarySidebarOpenOnMobile],
  );

  const onTaskClick = useCallback(
    (taskId: StudioChecklistTaskId) => {
      if (taskId === EXPLORE_PORTAL_TASK_ID) {
        setStatuses((prev) => ({
          ...prev,
          [taskId]: StudioChecklistTaskStatus.InProgress,
        }));
      }
      navigateAndCloseMobileSidebar(TASK_ROUTES[taskId]);
    },
    [navigateAndCloseMobileSidebar],
  );

  const tasks: StudioChecklistTask[] = useMemo(() => {
    const taskLabels: Record<StudioChecklistTaskId, string> = {
      publishApp: t(
        ($) => $.page_apps.app_builder.getting_started.tasks.publish_app,
      ),
      explorePortal: t(
        ($) => $.page_apps.app_builder.getting_started.tasks.explore_portal,
      ),
    };
    return STUDIO_CHECKLIST_TASK_IDS.map((id) => ({
      id,
      label: taskLabels[id],
      status: statuses[id] ?? StudioChecklistTaskStatus.Todo,
    }));
  }, [t, statuses]);

  const completedCount = tasks.filter(
    (task) => task.status === StudioChecklistTaskStatus.Done,
  ).length;
  const inProgressCount = tasks.filter(
    (task) => task.status === StudioChecklistTaskStatus.InProgress,
  ).length;
  // In-progress tasks count as half so the bar advances when a step is started.
  const completionPct = Math.round(
    ((completedCount + inProgressCount * 0.5) / tasks.length) * 100,
  );

  // Hide the whole checklist once "Explore the client experience" is complete.
  const explorePortalCompleted =
    statuses[EXPLORE_PORTAL_TASK_ID] === StudioChecklistTaskStatus.Done;

  return {
    tasks,
    completionPct,
    isCollapsed,
    toggleCollapse,
    onTaskClick,
    navigateAndCloseMobileSidebar,
    shouldRender: !explorePortalCompleted,
  };
};
